import { default as showQuickCompose } from '../actions/initActions/showQuickCompose';
import { lazyInitLoadMailboxAutomaticRepliesConfiguration } from '../loadAutomaticReplies/loadAutomaticRepliesIndex';
import shouldShowQuickComposePivots from '../utils/shouldShowQuickComposePivots';
import shouldShowTimeSuggestions from '../selectors/shouldShowTimeSuggestions';
import friendlyEndDateForAllDay from 'owa-calendar-forms-common/lib/utils/friendlyEndDateForAllDay';
import initFreeBusyCache from '../utils/initFreeBusyCache';
import initializeNewCalendarEvent from '../utils/initializeNewCalendarEvent';
import isEntrySourceUndo from '../utils/isEntrySourceUndo';
import { toJS } from 'mobx';
import { PerformanceDatapoint } from 'owa-analytics/lib/datapoints/PerformanceDatapoint';
import { dismissUndoNotification } from 'owa-calendar-actions';
import { lazyInitializeCalendarChannelPicker } from 'owa-calendar-channel-picker';
import { initializeLocationMetrics } from '../mutators/eventMetricsMutators';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { fetchFullCalendarEventAndUpdateItemResponseShapeType } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import initializeCalendarEvent from 'owa-calendar-forms-common/lib/utils/initializeCalendarEvent';
import { initializeTimeSuggestions } from 'owa-calendar-time-suggestions';
import { CalendarEvent, initializeAsThisAndFollowingInstances } from 'owa-calendar-types';
import initializeLocationFeedbackManagerState from 'owa-feedback-manager-actions/lib/actions/initializeLocationFeedbackManagerState';
import EventScope from 'owa-service/lib/contract/EventScope';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { ActionMessage, orchestrator } from 'satcheljs';
import {
    QUICK_COMPOSE_FORM_TYPE,
    QuickComposePivotType,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import {
    initializeCompose,
    logComposeFormOpenedTimes,
    updateOpenedForm,
    initializeTimeSuggestionMetricsInfo,
    logQuickComposePivotType,
} from 'owa-calendar-compose-form-actions';
import getCalendarComponent from '../utils/getCalendarComponent';
import { onShowQuickCompose } from '../actions/formOpenCloseNotificationActions';

orchestrator(showQuickCompose, async (actionMessage: ActionMessage) => {
    let entrySource = actionMessage.entrySource;
    let initialCalendarEvent = actionMessage.initialCalendarEvent;
    let eventScope = actionMessage.eventScope;

    let store = getComposeStore();

    const isUndo = isEntrySourceUndo(entrySource);

    // If the form's entry source is undo, the notification is dismissed when the undo button is
    // selected. In all other cases, we dismiss the notification manually and reset the store.
    if (!isUndo) {
        dismissUndoNotification();
    }

    // Log metrics
    const datapoint = new PerformanceDatapoint('ComposeEventStarted');
    datapoint.addCustomData({ EntrySource: entrySource });

    const showFreeBusyStatus = !isConsumer();

    if (isUndo) {
        updateOpenedForm(QUICK_COMPOSE_FORM_TYPE);

        // initialize FreeBusy Cache if needed
        if (store.event && showFreeBusyStatus) {
            initFreeBusyCache(store.event, QUICK_COMPOSE_FORM_TYPE);
        }

        initializeLocationMetrics();
    } else {
        initializeLocationFeedbackManagerState();
        let finalizedCalendarEvent: CalendarEvent;

        if (initialCalendarEvent?.ItemId?.Id) {
            // Editing an existing event
            if (
                eventScope == EventScope.AllInstancesInSeries ||
                eventScope == EventScope.ThisAndFollowingInstances
            ) {
                // Series master will not be in the cache, so we must go to server for it
                // Use fetchFullCalendarEventAndUpdateItemResponseShapeType with 'WithBlockedExternalContent'
                // to unblock external images since we are in a compose scenario.
                const {
                    fullEventPromise,
                } = fetchFullCalendarEventAndUpdateItemResponseShapeType(
                    initialCalendarEvent.ItemId,
                    'WithBlockedExternalContent',
                    null,
                    null /* getExistingCalendarEvent */,
                    true /** isfetchFullSeriesMasterCalendarEventFromEventInstance */,
                    { fetchSource: 'QuickCompose_' + entrySource }
                );

                // Block further initialization till fullEventPromise returns
                let seriesMaster: CalendarEvent = await fullEventPromise;

                // * Clone the item since it's coming from full item cache
                // * and we're modifying it below.
                seriesMaster = toJS(seriesMaster);

                if (eventScope == EventScope.ThisAndFollowingInstances) {
                    initializeAsThisAndFollowingInstances(
                        seriesMaster,
                        initialCalendarEvent as CalendarEvent
                    );
                }

                if (seriesMaster.IsAllDayEvent) {
                    seriesMaster.End = friendlyEndDateForAllDay(seriesMaster.End);
                }

                finalizedCalendarEvent = initializeCalendarEvent(
                    seriesMaster,
                    true /* isInitializedEvent */
                );
                initializeCompose(finalizedCalendarEvent, QUICK_COMPOSE_FORM_TYPE);
            } else {
                finalizedCalendarEvent = initializeCalendarEvent(
                    initialCalendarEvent,
                    true /* isInitializedEvent */
                );

                if (initialCalendarEvent.IsAllDayEvent) {
                    finalizedCalendarEvent.End = friendlyEndDateForAllDay(initialCalendarEvent.End);
                }

                // If we have an itemId, we must cache the event
                fetchFullCalendarEventAndUpdateItemResponseShapeType(
                    finalizedCalendarEvent.ItemId,
                    'WithBlockedExternalContent',
                    null,
                    null /* getExistingCalendarEvent */,
                    undefined,
                    { fetchSource: 'QuickCompose_' + entrySource }
                );
                initializeCompose(finalizedCalendarEvent, QUICK_COMPOSE_FORM_TYPE);
            }
        } else {
            // Creating a new event
            if (!initialCalendarEvent) {
                // New Button new event creation
                finalizedCalendarEvent = initializeNewCalendarEvent();
            } else {
                // Surface scenario
                finalizedCalendarEvent = initializeCalendarEvent(initialCalendarEvent);
            }

            initializeCompose(finalizedCalendarEvent, QUICK_COMPOSE_FORM_TYPE);

            if (shouldShowQuickComposePivots(false /* isEditMode */)) {
                logQuickComposePivotType(QuickComposePivotType.Event);
                lazyInitLoadMailboxAutomaticRepliesConfiguration.importAndExecute();
            }
        }

        // initialize FreeBusy Cache if needed
        if (finalizedCalendarEvent && showFreeBusyStatus) {
            initFreeBusyCache(finalizedCalendarEvent, QUICK_COMPOSE_FORM_TYPE);
        }
    }

    if (shouldShowTimeSuggestions()) {
        // If the suggestions flight is enabled, only then import the suggestions code
        initializeTimeSuggestions.importAndExecute(
            store.event,
            store.uiState.shouldAdjustStartTimeEndTime,
            getCalendarComponent() /* calendarComponent */
        );
        initializeTimeSuggestionMetricsInfo();
    }

    lazyInitializeCalendarChannelPicker.importAndExecute(store.event);
    logComposeFormOpenedTimes();
    datapoint.end();

    // Notify listeners that quick compose is ready, must be last call in this method
    onShowQuickCompose();
});
